// Findings as a queue a team works. A row is a case (lib/caseInbox.js
// formCases: what needs a person, joined across sessions), and what the team
// decided about it is a finding investigation (AlertTriageService), so every
// analyst reads the same owner, status and verdict, with who did what.
//
// A case finds its investigation without reading any investigation's
// members: the console creates it under an id derived from one of the case's
// findings, and a case looks up the ids of all of its findings. A case that
// gains or loses findings still finds it while the finding it was created
// under is in the case. An id the server refuses (one deleted before) is
// tried again under the next attempt's id.
//
// Nothing here calls the network; the hook in useConsoleInvestigations.js does.
import { severityRank } from "./triageLite.js";
import { parseServerTimestamp } from "./traceUtils.js";

// Triage first. A case someone holds is being investigated until it closes
// as a true positive or a false positive; a false positive can also close
// straight from triage, and a duplicate joins the investigation it repeats.
//
// The queue is one list, filtered by who holds a case (`assignee`): no filter
// at all; nobody, which is triage; the reader; anyone; or one person, by user id. Closed
// cases join it only when asked for (`closed`), under whoever held them.
export const ASSIGNEES = ["all", "none", "me", "any"];
export const ASSIGNEE_LABEL = { all: "All findings", none: "Unassigned", me: "You", any: "Anyone" };
export const SORTS = ["severity", "newest", "oldest"];
export const SORT_LABEL = { severity: "Severity, then newest", newest: "Newest first", oldest: "Oldest first" };

// Where review left a finding that it judged needs no person. The console
// lists these only for staff auditing what triage drops (`cleared`); one the
// team reopened is a case like any other.
export const CLEARED_GROUPS = new Set(["didnt_happen", "not_an_action", "allowed", "contained"]);

export const CLOSE_REASONS = {
    expected: "Expected activity",
    wrong: "Detection was wrong",
    duplicate: "Duplicate of another case",
    other: "Other reason",
};
// The note each decision records, which the activity log shows and which is
// how a closed case without a verdict says why it was closed; what the
// analyst wrote follows it on the next line. The false positive notes keep
// their earlier wording so notes already saved still name their reason;
// `resolve`, `confirm` and `duplicate` are read from earlier decisions only.
export const DECISION_NOTE = {
    truePositive: "True positive",
    resolve: "Resolved",
    confirm: "Confirmed: needs action",
    expected: "Not a problem: expected activity",
    wrong: "Not a problem: the detection was wrong",
    duplicate: "Not a problem: duplicate of another case",
    joined: "Duplicate of another investigation",
    added: "Added a duplicate",
    saved: "Saved the case's findings",
    inconclusive: "Inconclusive",
    other: "Rejected",
    reopen: "Reopened",
};

// An investigation closed without a verdict either way says so in its
// summary, which the list returns (its notes are only in its activity): the
// API's verdicts are true positive and false positive only.
export const INCONCLUSIVE_SUMMARY = "Inconclusive";
// A case rejected for a reason of the analyst's own (not a false positive,
// expected activity or a duplicate) says so the same way.
export const REJECTED_SUMMARY = "Rejected";

// What a close did not check, said on its note's last line.
const NOT_CHECKED = "Not checked: ";

/** A decision's note with what the analyst wrote, if anything, and what a close did not check. */
export const noteWith = (prefix, text, notChecked = "") => [prefix, String(text || "").trim(), String(notChecked || "").trim() && `${NOT_CHECKED}${String(notChecked).trim()}`].filter(Boolean).join("\n");

/** A note's words, apart from what it says was not checked. */
export function notCheckedOf(text) {
    const s = String(text || "");
    const m = /(?:^|\n)Not checked: ([^\n]*)$/.exec(s);
    return m ? { words: s.slice(0, m.index), notChecked: m[1] } : { words: s, notChecked: "" };
}

/** Which decision a note records and what the analyst wrote with it, or null for a note of their own. */
export function readNote(note) {
    const s = String(note || "");
    for (const [key, prefix] of Object.entries(DECISION_NOTE)) {
        if (s === prefix) return { key, text: "" };
        if (s.startsWith(`${prefix}\n`)) return { key, text: s.slice(prefix.length + 1) };
    }
    return null;
}

// A case closed as a duplicate names, in its investigation's summary, the
// investigation it joined, so a list read says where it went.
const JOINED = "Joined: ";
export const joinSummary = (inv) => `${JOINED}${String(inv.title || "Untitled case").slice(0, 200)} [${inv.incidentId}]`;
export function joinedTo(summary) {
    const m = /^Joined: ([\s\S]*) \[([0-9a-f-]{36})\]$/i.exec(String(summary || ""));
    return m ? { title: m[1], id: m[2].toLowerCase() } : null;
}

// Additions per save, as the API bounds them.
export const ADD_LIMIT = 100;
// The longest review period one addition may name.
const MAX_PERIOD_MS = 90 * 86_400_000;
// Ids tried per finding before a create gives up.
export const ATTEMPTS = 3;

const lower = (v) => String(v ?? "").trim().toLowerCase();

// -- ids ------------------------------------------------------------------------

const NAMESPACE = "origin.findings-console.case.v1";
function fnv32(text, seed) {
    let h = (0x811c9dc5 ^ seed) >>> 0;
    for (let i = 0; i < text.length; i++) {
        h ^= text.charCodeAt(i);
        h = Math.imul(h, 0x01000193) >>> 0;
    }
    return h >>> 0;
}
const SEEDS = [0x9e3779b9, 0x85ebca6b, 0xc2b2ae35, 0x27d4eb2f];
const idCache = new Map();

/** The investigation id a case created under `findingId` takes (a version 8 UUID). */
export function caseInvestigationId(findingId, attempt = 0) {
    const key = `${lower(findingId)}#${attempt}`;
    const hit = idCache.get(key);
    if (hit) return hit;
    const hex = SEEDS.map((seed) => fnv32(`${NAMESPACE}:${key}`, seed).toString(16).padStart(8, "0")).join("");
    const variant = ((parseInt(hex[16], 16) & 0x3) | 0x8).toString(16);
    const v = `${hex.slice(0, 12)}8${hex.slice(13, 16)}${variant}${hex.slice(17)}`;
    const id = `${v.slice(0, 8)}-${v.slice(8, 12)}-${v.slice(12, 16)}-${v.slice(16, 20)}-${v.slice(20, 32)}`;
    if (idCache.size > 20_000) idCache.clear();
    idCache.set(key, id);
    return id;
}

/**
 * The investigation that records a case, from investigations keyed by id, or
 * null. A case whose findings are in more than one (a duplicate that joined
 * it) reads the open one, else one that is not a duplicate's.
 */
export function investigationFor(findingIds, byId) {
    if (!byId?.size) return null;
    let found = null;
    let joined = null;
    for (const f of findingIds || []) {
        for (let n = 0; n < ATTEMPTS; n++) {
            const inv = byId.get(caseInvestigationId(f, n));
            if (!inv) continue;
            if (inv.status === "open") return inv;
            if (joinedTo(inv.summary)) joined ||= inv; else found ||= inv;
        }
    }
    return found || joined;
}

// -- state ----------------------------------------------------------------------

/**
 * How many of a case's findings were reviewed after its investigation closed:
 * findings no decision saw. Zero for an open investigation.
 */
export function newSinceClosed(inv, view) {
    if (inv?.status !== "closed" || !Number.isFinite(inv.closedAtMs)) return 0;
    return (view?.items || []).filter((p) => parseServerTimestamp(p.finding.completedAt) > inv.closedAtMs).length;
}

/**
 * Where the team left a case: `new` (in triage: no one holds it),
 * `investigating` (someone holds it), `tp` (closed as a true positive),
 * `joined` (closed as a duplicate of the investigation `joined` names) or
 * `closed` (a false positive; `reason` "wrong" where the verdict says the
 * detection was wrong, "inconclusive" or "other" where its summary says it was
 * closed so, else null until the note is read). A closed case with
 * `fresh` findings reviewed since it closed is back in triage.
 */
export function caseState(inv, fresh = 0) {
    const none = { owner: null, verdict: null, reason: null, fresh: 0, joined: null };
    if (!inv) return { state: "new", ...none };
    const owner = inv.ownerUserId || null;
    const verdict = inv.outcome || inv.verdict || null;
    const base = { ...none, owner, verdict };
    if (inv.status !== "closed") return { ...base, state: owner ? "investigating" : "new" };
    if (fresh > 0) return { ...base, state: "new", fresh };
    if (verdict === "true_positive") return { ...base, state: "tp" };
    if (verdict === "mixed" || verdict === "inconclusive") return { ...base, state: "closed", reason: verdict };
    const joined = joinedTo(inv.summary);
    if (joined) return { ...base, state: "joined", joined };
    if (inv.summary === INCONCLUSIVE_SUMMARY) return { ...base, state: "closed", reason: "inconclusive" };
    if (inv.summary === REJECTED_SUMMARY) return { ...base, state: "closed", reason: "other" };
    return { ...base, state: "closed", reason: verdict === "false_positive" ? "wrong" : null };
}

/** The reason a closed case's latest decision note names, or null. */
export function reasonFromNote(note) {
    const text = String(note || "");
    for (const [reason, prefix] of Object.entries(DECISION_NOTE)) {
        if (["expected", "wrong", "duplicate", "other"].includes(reason) && text.startsWith(prefix)) return reason;
    }
    return null;
}

export const isOpenState = (state) => state === "new" || state === "investigating";

/** Whether a decision applies where the team left an entry, so one made on many skips the rest. */
export function canDecide(entry, action) {
    if (!entry) return false;
    const { state } = entry;
    // What review cleared is a case only once someone reopens it as one.
    if (state === "cleared") return action === "reopen";
    if (action === "take") return state === "new";
    if (action === "truePositive" || action === "putBack" || action === "inconclusive") return state === "investigating";
    if (action === "close" || action === "assign" || action === "join") return isOpenState(state);
    if (action === "reopen") return !isOpenState(state);
    return action === "note";
}

/**
 * Whether an entry is in the list a filter ({ assignee, closed }) shows.
 * Triage holds what nobody has taken, whoever held it before it came back
 * with new findings. `me` is the reader's user id.
 */
export function inFilter(entry, { assignee = "all", closed = false, cleared = false } = {}, me = null) {
    // What review cleared is nobody's: listed with any assignee when asked for.
    if (entry.state === "cleared") return cleared;
    const open = isOpenState(entry.state);
    if (!open && !closed) return false;
    if (assignee === "all") return true;
    if (entry.state === "new") return assignee === "none";
    if (assignee === "none") return !entry.owner;
    if (assignee === "any") return !!entry.owner;
    const who = assignee === "me" ? me : assignee;
    return !!who && entry.owner === who;
}

/**
 * The filter that lists an entry, for an old link or a joined case to open
 * in: `closed` only for a closed one, so the reader's choice otherwise stands.
 * A teammate's case opens under Anyone, never a filter on that one teammate.
 */
export function filterFor(entry, me) {
    if (entry.state === "cleared") return { cleared: true };
    if (entry.state === "new") return { assignee: "none" };
    const assignee = !entry.owner ? "none" : entry.owner === me ? "me" : "any";
    return isOpenState(entry.state) ? { assignee } : { assignee, closed: true };
}

/** The list's heading for an assignee. `nameOf(userId)` names a person. */
export function filterTitle(assignee, nameOf = () => "") {
    if (assignee === "all") return "All findings";
    if (assignee === "none") return "Unassigned";
    if (assignee === "me") return "Assigned to you";
    if (assignee === "any") return "Assigned to anyone";
    return `Assigned to ${nameOf(assignee) || "a teammate"}`;
}

// -- search ---------------------------------------------------------------------

// The keyword search: every word typed is found, ignoring case, somewhere in
// what a case says (its title, sentence, endpoint, user, standards, severity,
// status and owner), as `caseSearchText` puts it together once per case.
export function searchWords(query) {
    return lower(query).split(/\s+/).filter(Boolean);
}

/**
 * What a case's search looks through: its title, the sentence and `texts`
 * (each of its findings' titles and what review said of it), its endpoint and
 * who is registered there, `names` (the agents and models its traces ran),
 * `shown` (what the case showed once opened: its story, notes and who and
 * what was involved), its standards, severity, state and owner.
 */
export function caseSearchText(entry, { peek = "", texts = [], names = [], shown = "", machine = null, state = "", owner = "" } = {}) {
    return lower([entry.title, peek, ...texts, machine?.host, machine?.user, ...names, shown, ...(entry.standards || []), entry.severity, state, owner].filter(Boolean).join("\n"));
}

export function passesWords(text, words) {
    return words.every((w) => text.includes(w));
}

// The quick filters over a view's list, by severity or by first being seen
// today (a case that only grew today is not new). The filter (`quick`) is
// "all", "today", one severity, or the severities picked together (an array).
export const QUICK = ["all", "critical", "high", "medium", "low", "today"];
export const QUICK_LABEL = { all: "All", critical: "Critical", high: "High", medium: "Medium", low: "Low", today: "New today" };
const QUICK_SEVERITIES = new Set(["critical", "high", "medium", "low"]);

/** The filter once the quick filter `id` is clicked: All and New today alone; a severity added to or taken from those picked, none picked being All. */
export function toggleQuick(quick, id) {
    if (!QUICK_SEVERITIES.has(id)) return id;
    const picked = Array.isArray(quick) ? quick : QUICK_SEVERITIES.has(quick) ? [quick] : [];
    const next = picked.includes(id) ? picked.filter((s) => s !== id) : [...picked, id];
    return next.length ? QUICK.filter((q) => next.includes(q)) : "all";
}

/** The quick filters pressed for a filter (`quick`). */
export const quickPressed = (quick) => (Array.isArray(quick) ? quick : [quick]);

export function passesQuick(entry, quick, dayStartMs) {
    if (Array.isArray(quick)) return quick.includes(lower(entry.severity));
    if (QUICK_SEVERITIES.has(quick)) return lower(entry.severity) === quick;
    if (quick === "today") {
        const first = Number.isFinite(entry.firstAt) ? entry.firstAt : entry.latestAt;
        return Number.isFinite(first) && first >= dayStartMs;
    }
    return true;
}

// -- order ----------------------------------------------------------------------

export function sortEntries(list, by = "severity") {
    const latest = (e) => (Number.isFinite(e.latestAt) ? e.latestAt : 0);
    const first = (e) => (Number.isFinite(e.firstAt) ? e.firstAt : latest(e));
    return [...list].sort((a, b) => (by === "newest" ? latest(b) - latest(a)
        : by === "oldest" ? first(a) - first(b)
            : (severityRank(b.severity) - severityRank(a.severity)) || (latest(b) - latest(a)))
        || String(a.id).localeCompare(String(b.id)));
}

// -- entries ----------------------------------------------------------------------

/**
 * The console's rows: each formed case with where the team left it. A finding
 * review cleared is not one, unless the team reopened it as a case earlier:
 * that is a case of its own (`caseOf(placed)` makes its view), so its
 * investigation stays in sight.
 */
export function consoleEntries({ cases = [], placed = [], byId = new Map(), caseOf, cleared = false }) {
    const out = [];
    const inCase = new Set();
    for (const view of cases) {
        for (const id of view.findingIds) inCase.add(lower(id));
        out.push(caseEntry(view, investigationFor(view.findingIds, byId)));
    }
    for (const p of placed) {
        if (!CLEARED_GROUPS.has(p.group) || inCase.has(lower(p.finding.id)) || !caseOf) continue;
        const inv = investigationFor([p.finding.id], byId);
        if (inv) out.push(caseEntry(caseOf(p), inv));
        else if (cleared) out.push(clearedEntry(caseOf(p), p.group));
    }
    return out;
}

// A finding review cleared, for staff auditing what triage drops: a case of
// one in shape, under the id it keeps once reopened as a case, saying why
// review cleared it (`cleared`, its group).
function clearedEntry(view, group) {
    return {
        id: view.id, view, inv: null, state: "cleared", cleared: group, owner: null, verdict: null, reason: null, fresh: 0, joined: null,
        severity: view.severity, title: view.title, latestAt: view.latestAt, firstAt: view.firstAt, standards: [],
    };
}

/** When a finding's act happened: its earliest cited message (`messageAt`: message id -> ms), or null. */
export function happenedAt(finding, messageAt) {
    let t = Infinity;
    for (const m of finding?.messages || []) {
        const at = messageAt?.get(lower(m));
        if (at < t) t = at;
    }
    return Number.isFinite(t) ? t : null;
}

/**
 * The entries timed by when their acts happened: a case's first and latest
 * (`firstAt`, `latestAt`), with `timed`. An entry none of whose messages is
 * known yet keeps its review times, for order only, and is not `timed`.
 */
export function withEventTimes(entries, messageAt) {
    if (!entries) return entries;
    return entries.map((e) => {
        const times = e.view.items.map((p) => happenedAt(p.finding, messageAt)).filter((t) => t != null);
        return times.length ? { ...e, timed: true, firstAt: Math.min(...times), latestAt: Math.max(...times) } : { ...e, timed: false };
    });
}

function caseEntry(view, inv) {
    const s = caseState(inv, newSinceClosed(inv, view));
    return {
        id: view.id, view, inv, ...s,
        severity: view.severity, title: view.title, latestAt: view.latestAt, firstAt: view.firstAt, standards: view.metBy || [],
    };
}

// -- writes -----------------------------------------------------------------------

/**
 * What a decision changes on an investigation (the fields of
 * saveFindingInvestigation). `text` is what the analyst wrote: why it is a
 * false positive, where a true positive's response went, or a note.
 * `target` is the investigation a duplicate joins.
 */
export function changeFor(action, { me = null, owner = null, reason = null, text = "", notChecked = "", target = null, previous = null } = {}) {
    // A decision on a case that had joined another, or been closed as
    // inconclusive or rejected, no longer says so.
    const unjoin = joinedTo(previous?.summary) || [INCONCLUSIVE_SUMMARY, REJECTED_SUMMARY].includes(previous?.summary) ? { summary: "" } : {};
    const byMe = previous?.owner ? {} : { ownerUserId: me };
    switch (action) {
    case "take": return { ownerUserId: me, status: "open", ...unjoin };
    case "assign": return { ownerUserId: owner || me, status: "open", ...unjoin };
    case "putBack": return { ownerUserId: null, status: "open" };
    // A true positive is what an investigation concludes; the response is
    // made outside the console.
    case "truePositive": return { status: "closed", verdict: "true_positive", note: noteWith(DECISION_NOTE.truePositive, text, notChecked), ...byMe, ...unjoin };
    case "close": return { status: "closed", verdict: reason === "wrong" ? "false_positive" : null, note: noteWith(DECISION_NOTE[reason] || DECISION_NOTE.expected, text, notChecked), ...byMe, ...unjoin, ...(reason === "other" ? { summary: REJECTED_SUMMARY } : {}) };
    case "join": return { status: "closed", verdict: null, summary: joinSummary(target), note: noteWith(DECISION_NOTE.joined, target.title), ...byMe };
    // Closed with no verdict either way: what is unresolved goes in its note.
    case "inconclusive": return { status: "closed", verdict: null, summary: INCONCLUSIVE_SUMMARY, note: noteWith(DECISION_NOTE.inconclusive, text, notChecked), ...byMe };
    case "note": return { note: String(text).trim() };
    case "reopen": return { status: "open", verdict: null, ownerUserId: null, note: DECISION_NOTE.reopen, ...unjoin };
    // Back to where it was before a decision, for Undo.
    case "revert": return { status: previous?.status || "open", verdict: previous?.verdict || null, ownerUserId: previous?.owner || null, ...(previous?.summary != null ? { summary: previous.summary } : {}) };
    default: throw new Error(`Unknown decision: ${action}`);
    }
}

/**
 * The review period an addition names: the findings' completion times with
 * a minute either side, at most 90 days ending at the newest. Findings older
 * than that are left out (`dropped`).
 */
export function periodFor(items) {
    const times = (items || []).map((p) => ({ id: p.finding.id, at: parseServerTimestamp(p.finding.completedAt) })).filter((x) => Number.isFinite(x.at));
    if (!times.length) return null;
    const end = Math.max(...times.map((x) => x.at)) + 60_000;
    const earliest = Math.min(...times.map((x) => x.at)) - 60_000;
    const start = Math.max(earliest, end - MAX_PERIOD_MS + 1000);
    const kept = times.filter((x) => x.at >= start).map((x) => x.id);
    return {
        timeRange: { absolute: { start: new Date(start).toISOString(), end: new Date(end).toISOString() } },
        findingIds: kept,
        dropped: times.length - kept.length,
    };
}

/** The additions a new investigation for these findings is made of, ADD_LIMIT at a time. */
export function additionBatches(findingIds) {
    const ids = [...new Set((findingIds || []).map(lower))];
    const out = [];
    for (let i = 0; i < ids.length; i += ADD_LIMIT) out.push(ids.slice(i, i + ADD_LIMIT));
    return out;
}

/** A short name for an owner from the assignee list's email, else a placeholder. */
export function ownerLabel(userId, users, me) {
    if (!userId) return "";
    if (me && userId === me) return "You";
    const email = users?.get?.(userId) || "";
    return email ? email.split("@")[0] : "A teammate";
}
